import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  Check,
  CheckCircle,
  EnvelopeOpen,
  CreditCard,
  Sun,
  Moon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { notifications as notifApi } from "@/api";
import { updateMe } from "@/api/auth";
import { useAuth } from "@/context/AuthContext";
import {
  normalizeActaTheme,
  useTheme,
} from "@/context/ThemeContext";

const ICONS = {
  reopen_request: EnvelopeOpen,
  plan_activated: CreditCard,
  "share.message_received": EnvelopeOpen,
  share_message_received: EnvelopeOpen,
};


// acta_patch_notification_message_redirect_v1
function firstNotificationValue(notification, ...keys) {
  for (const source of [notification, notification?.payload, notification?.metadata, notification?.payload?.metadata]) {
    if (!source || typeof source !== "object") continue;

    for (const key of keys) {
      const value = source[key];
      if (value) return value;
    }
  }

  return null;
}

function buildNotificationTarget(notification) {
  const direct =
    firstNotificationValue(notification, "link", "url", "targetUrl", "href") ||
    null;

  if (direct && direct !== "#") return direct;

  const prospectId = firstNotificationValue(
    notification,
    "prospectId",
    "prospect_id",
    "prospect"
  );

  if (!prospectId) return "#";

  const shareId = firstNotificationValue(notification, "shareId", "share_id", "share");
  const messageId = firstNotificationValue(notification, "messageId", "message_id", "message");

  const params = new URLSearchParams();
  params.set("tab", "messages");

  if (shareId) params.set("shareId", shareId);
  if (messageId) params.set("messageId", messageId);

  return `/app/commerce/prospects/${encodeURIComponent(prospectId)}?${params.toString()}`;
}




// acta_fix_notification_message_exact_redirect_v2
function actaNotificationValueV2(notification, ...keys) {
  const sources = [
    notification,
    notification?.payload,
    notification?.metadata,
    notification?.data,
    notification?.payload?.metadata,
    notification?.payload?.data,
    notification?.payload?.message,
    notification?.payload?.share,
  ];

  for (const source of sources) {
    if (!source || typeof source !== "object") {
      continue;
    }

    for (const key of keys) {
      if (source[key]) {
        return source[key];
      }
    }
  }

  return "";
}

function actaMessageNotificationTargetV2(notification) {
  const direct =
    actaNotificationValueV2(
      notification,
      "targetUrl",
      "url",
      "link",
      "href"
    );

  if (
    direct &&
    String(direct).includes("tab=messages")
  ) {
    return String(direct)
      .replace("/app/commerce/prospects/", "/app/commerce/prospects/")
      .replace("share_id=", "share_id=")
      .replace("message_id=", "message_id=");
  }

  const prospectId =
    actaNotificationValueV2(
      notification,
      "prospectId",
      "prospect_id"
    );

  const shareId =
    actaNotificationValueV2(
      notification,
      "shareId",
      "share_id"
    );

  const messageId =
    actaNotificationValueV2(
      notification,
      "messageId",
      "message_id",
      "id"
    );

  if (!prospectId) {
    return direct || "#";
  }

  const params = new URLSearchParams({
    tab: "messages",
  });

  if (shareId) {
    params.set("share_id", shareId);
  }

  if (messageId) {
    params.set("message_id", messageId);
  }

  return `/app/commerce/prospects/${encodeURIComponent(
    prospectId
  )}?${params.toString()}`;
}


// ACTA_NOTIFICATION_CLICK_REDIRECT_V1
function actaNotificationEventKey(notification) {
  return (
    notification?.eventKey ||
    notification?.event_key ||
    notification?.kind ||
    notification?.type ||
    notification?.payload?.eventKey ||
    notification?.payload?.event_key ||
    notification?.payload?.kind ||
    notification?.payload?.type ||
    ""
  );
}

function actaNormalizeNotificationTarget(notification) {
  const eventKey = actaNotificationEventKey(notification);
  let target = actaNotificationTargetV2(notification);

  if (!target || target === "#") {
    const prospectId = actaNotificationValueV2(
      notification,
      "prospectId",
      "prospect_id",
      "prospect"
    );
    const shareId = actaNotificationValueV2(notification, "shareId", "share_id", "share");

    if (prospectId) {
      const params = new URLSearchParams();
      if (
        eventKey === "share_meeting_selected" ||
        eventKey === "share.meeting_selected" ||
        eventKey === "meeting_selected"
      ) {
        params.set("tab", "meetings");
      }
      if (shareId) params.set("shareId", shareId);

      target = `/app/commerce/prospects/${encodeURIComponent(prospectId)}${
        params.toString() ? `?${params.toString()}` : ""
      }`;
    }
  }

  if (!target || target === "#") return "#";

  target = String(target).replace("/app/prospects/", "/app/commerce/prospects/");

  if (
    eventKey === "share_meeting_selected" ||
    eventKey === "share.meeting_selected" ||
    eventKey === "meeting_selected"
  ) {
    const [base, query = ""] = target.split("?");
    const params = new URLSearchParams(query);
    if (!params.get("tab")) params.set("tab", "meetings");
    target = `${base}?${params.toString()}`;
  }

  return target;
}

function actaNavigateNotificationTarget(target, navigate) {
  if (!target || target === "#") return;

  if (target.startsWith("http://") || target.startsWith("https://")) {
    window.location.assign(target);
    return;
  }

  navigate(target);
}

function actaNotificationTargetV2(notification) {
  const eventKey =
    notification?.eventKey ||
    notification?.event_key ||
    notification?.type ||
    notification?.payload?.eventKey ||
    notification?.payload?.event_key;

  if (
    eventKey === "share.message_received" ||
    eventKey === "share_message_received"
  ) {
    return actaMessageNotificationTargetV2(notification);
  }

  return (
    notification?.link ||
    notification?.targetUrl ||
    notification?.url ||
    notification?.payload?.targetUrl ||
    notification?.payload?.url ||
    "#"
  );
}

export default function NotificationsDropdown() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const {
    theme,
    themeOwnerId,
    setThemeForUser,
  } = useTheme();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unreadCount: 0 });
  const [themeSaving, setThemeSaving] = useState(false);
  const ref = useRef(null);

  const isSupervisionSpace =
    location.pathname.startsWith(
      "/monpremiermonitoring.fr"
    );
  const currentUserId = String(user?.id || "");
  const contextBelongsToCurrentUser =
    currentUserId && themeOwnerId === currentUserId;
  const accountTheme = normalizeActaTheme(
    user?.themePreference || "light"
  );
  const appliedTheme = isSupervisionSpace
    ? "dark"
    : normalizeActaTheme(
        contextBelongsToCurrentUser
          ? theme
          : accountTheme
      );

  const load = async () => {
    try {
      const next = await notifApi.list();
      setData(next);
    } catch {}
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    await notifApi.markAllRead();
    load();
  };

  const handleMobileThemeToggle = async () => {
    if (!currentUserId || themeSaving || isSupervisionSpace) {
      return;
    }

    const previousTheme = appliedTheme;
    const nextTheme =
      appliedTheme === "dark"
        ? "light"
        : "dark";

    setThemeForUser(currentUserId, nextTheme);
    setThemeSaving(true);

    try {
      await updateMe({
        theme_preference: nextTheme,
      });
    } catch (error) {
      setThemeForUser(currentUserId, previousTheme);
      toast.error(
        "Impossible d’enregistrer le thème. Le choix précédent a été restauré."
      );
    } finally {
      setThemeSaving(false);
    }
  };

  const onItemClick = async (event, n) => {
    event.preventDefault();

    const target = actaNormalizeNotificationTarget(n);
    setOpen(false);

    if (!n.read) {
      notifApi.markRead(n.id)
        .then(load)
        .catch(() => {});
    }

    actaNavigateNotificationTarget(target, navigate);
  };

  return (
    <>
      {!isSupervisionSpace && (
        <button
          type="button"
          onClick={handleMobileThemeToggle}
          disabled={themeSaving}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink-soft)] transition-colors hover:text-[var(--do-accent)] disabled:opacity-50 lg:hidden"
          aria-label={
            appliedTheme === "dark"
              ? "Activer le thème jour"
              : "Activer le thème nuit"
          }
          title={
            appliedTheme === "dark"
              ? "Activer le thème jour"
              : "Activer le thème nuit"
          }
          data-testid="mobile-theme-toggle"
        >
          {appliedTheme === "dark" ? (
            <Sun size={18} weight="bold" />
          ) : (
            <Moon size={18} weight="bold" />
          )}
        </button>
      )}

      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen((v) => !v)}
          className="relative text-[var(--do-muted)] hover:text-[var(--lirah-primary)] transition-colors"
          data-testid="notifications-btn"
          title="Notifications"
        >
          <Bell size={18} weight={open ? "fill" : "regular"} />
          {data.unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-[var(--lirah-danger)] text-[var(--do-on-accent)] text-[9px] font-bold flex items-center justify-center" data-testid="notifications-badge">
              {data.unreadCount}
            </span>
          )}
        </button>
        {open && (
          <div
            className="fixed left-2 right-2 top-[calc(3.75rem+env(safe-area-inset-top,0px))] z-[130] flex max-h-[calc(100dvh-4.5rem-env(safe-area-inset-bottom,0px))] min-w-0 flex-col overflow-hidden rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] shadow-[var(--do-shadow-card)] lg:absolute lg:left-auto lg:right-0 lg:top-full lg:mt-2 lg:w-[380px] lg:max-w-[calc(100vw-2rem)] lg:max-h-[min(420px,calc(100dvh-6rem))] lg:rounded-[6px]"
            data-testid="notifications-dropdown"
          >
            <div className="flex min-w-0 shrink-0 items-start justify-between gap-3 border-b border-[var(--do-border)] px-3 py-2.5 sm:px-4 sm:py-3">
              <div className="min-w-0">
                <div className="font-heading font-bold text-sm">Notifications</div>
                <div className="text-[11px] text-[var(--do-muted)] mt-0.5">{data.unreadCount} non lue{data.unreadCount > 1 ? "s" : ""}</div>
              </div>
              {data.unreadCount > 0 && (
                <button
                  onClick={markAll}
                  className="shrink-0 text-right text-xs leading-4 text-[var(--lirah-primary)] hover:underline"
                  data-testid="mark-all-read"
                >
                  Tout marquer comme lu
                </button>
              )}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {data.notifications.length === 0 ? (
                <div className="p-8 text-center text-sm text-[var(--do-muted)]">
                  <CheckCircle size={24} weight="duotone" className="text-[var(--do-muted)] mx-auto mb-2" />
                  Vous êtes à jour !
                </div>
              ) : (
                <ul>
                  {data.notifications.map((n) => {
                    const Icon = ICONS[n.kind] || Bell;
                    return (
                      <li key={n.id}>
                        <Link
                          to={actaNormalizeNotificationTarget(n)}
                          onClick={(event) => onItemClick(event, n)}
                          data-testid={`notif-${n.id}`}
                          className={`flex items-start gap-2.5 border-b border-[var(--do-border)] px-3 py-2.5 hover:bg-[var(--do-bg-solid)] last:border-0 sm:gap-3 sm:px-4 sm:py-3 ${!n.read ? "bg-[var(--lirah-primary-soft)]" : ""}`}
                        >
                          <div className="h-8 w-8 rounded-[4px] bg-[var(--do-bg-solid)] border border-[var(--do-border)] flex items-center justify-center shrink-0">
                            <Icon size={14} weight="duotone" className="text-[var(--lirah-primary)]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium leading-tight">{n.title}</div>
                            {n.body && <div className="text-xs text-[var(--do-muted)] mt-1 line-clamp-2">{n.body}</div>}
                            <div className="text-[10px] text-[var(--do-muted)] font-mono-stat uppercase tracking-wider mt-1">
                              {new Date(n.createdAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                            </div>
                          </div>
                          {!n.read && <div className="h-2 w-2 rounded-full bg-[var(--lirah-primary)] mt-2 shrink-0" />}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}


// acta_fix_notification_message_safe_redirect_v3
// Les notifications message doivent pointer vers Commerce > Prospect > Messages
// avec share_id/message_id.